/**
 * CEO Big Numbers Dashboard
 *
 * Executive-level financial overview extracted from the main dashboard route.
 * Displays comprehensive financial metrics, charts, and client analytics.
 *
 * Features:
 * - URL-backed filter state management
 * - DRE data integration
 * - PROA comparison
 * - Revenue & cost metric cards
 * - Performance charts (Revenue vs Cost, Waterfall, Cost Distribution)
 * - Client ranking and margin analysis
 */

import { trpc } from '@/api/trpc'
import {
  DREControlsPanel,
  calculateComparisonRange,
  calculatePeriodDates
} from '@/app/financial/dre/components'
import { useFilterSync } from '@/app/financial/shared/hooks/useFilterSync'
import { ClientRankingTable } from '@/app/painel-gerencial/components/client-ranking-table'
import { CostDistributionChart } from '@/app/painel-gerencial/components/cost-distribution-chart'
import { RevenueCostChart } from '@/app/painel-gerencial/components/revenue-cost-chart'
import EnhancedMetricCard, { type MetricCardAdjustmentItem } from '@/components/financial/EnhancedMetricCard'
import { SaudeMonumentaSection, MargensHealthCard } from '@/components/ceo/SaudeMonumentaSection'
import { countBusinessDays } from '@/components/ceo/TimesheetFillRateCard'
import { BURevenueSplitInline } from '@/components/ceo/BURevenueSplitInline'
import { FluxoFinanceiroSankey } from '@/components/ceo/FluxoFinanceiroSankey'
import { useDREConsolidatedData, useMonthlyChartData, toNum } from '@/lib/services/financial-analytics.service'
import { useTrendAnalysis } from '@/lib/services/trend-analysis.service'
import { useFolhaGapEstimate } from '@/lib/services/folha-gap.service'
import { useCommittedTerceiros } from '@/lib/services/committed-costs.service'
import { useEncarteirados } from '@/lib/services/encarteirados.service'
import { EncarteiradosModal } from '@/components/ceo/EncarteiradosModal'
import { usePageTitle } from '@/stores/page-title-store'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import { formatCurrency } from '@/lib/financial-utils'
import { AlertTriangle, Calculator, HelpCircle, TrendingUp } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

// Get default YTD date range
function getDefaultDateRange() {
  return calculatePeriodDates('ytd')
}

export function CEOBigNumbersDashboard() {
  usePageTitle('Big Numbers')

  // Get default dates for filter initialization
  const defaultDates = getDefaultDateRange()

  // Use filter sync for URL-backed state management (FR1, FR2 - PRD Week 50)
  const { state: filterState, setFilter, setFilters } = useFilterSync({
    defaults: {
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      granularity: 'accumulated', // Default YTD accumulated
      comparisonMode: 'same_period_last_year',
      chartType: 'bar',
      selectedDimensions: [],
    },
  })

  // Extract filter values
  const { startDate, endDate, granularity, comparisonMode } = filterState

  // Calculate comparison date range based on comparison mode
  const comparisonRange = useMemo(
    () => calculateComparisonRange(startDate, endDate, comparisonMode),
    [startDate, endDate, comparisonMode]
  )

  // Get comparison period dates (for variance calculation)
  const previousStartDate = comparisonRange?.startDate || ''
  const previousEndDate = comparisonRange?.endDate || ''

  // Get current year for PROA fetching
  const currentYear = parseInt(startDate.substring(0, 4), 10) || new Date().getFullYear()

  // Period key for contextual notes (metric + period scoping)
  const periodKey = `${startDate}_${endDate}`

  // Use shared service for DRE data consolidation
  const {
    consolidatedData,
    dreData,
    officialProa,
    isLoading: isDreLoading,
    error: dreError,
    refetch: refetchDre,
  } = useDREConsolidatedData(
    startDate,
    endDate,
    previousStartDate,
    previousEndDate,
    currentYear,
    true // enabled
  )

  // Lucro Ajustado inputs: missing folha months + committed third-party costs
  const folhaGap = useFolhaGapEstimate({ endDate, enabled: !!endDate })
  const committedTerceiros = useCommittedTerceiros()
  const encarteirados = useEncarteirados()
  const [encarteiradosModalOpen, setEncarteiradosModalOpen] = useState(false)

  const lucroAdjustment = useMemo(() => {
    if (!consolidatedData) return undefined
    const items: MetricCardAdjustmentItem[] = []
    for (const m of folhaGap.missingMonths) {
      items.push({
        label: `Folha ${m.monthLabel}`,
        value: m.estimate,
        isApproximate: m.isApproximate,
      })
    }
    if (committedTerceiros.value > 0) {
      items.push({
        label: 'Custos não pagos',
        value: committedTerceiros.value,
        breakdown: committedTerceiros.breakdown,
      })
    }
    if (items.length === 0) return undefined
    return {
      items,
      adjustedLabel: 'Ajustado',
    }
  }, [consolidatedData, folhaGap.missingMonths, committedTerceiros.value])

  // Total deduction from Lucro YTD — shared by the Lucro card, Saúde da
  // Monumenta (PROA Lucro, Margens), and the year-end projection trend.
  // Ensures all lucro-based numbers tell the same story.
  const lucroTotalDeduction = useMemo(() => {
    if (!lucroAdjustment) return 0
    return lucroAdjustment.items.reduce((s, it) => s + it.value, 0)
  }, [lucroAdjustment])

  // Custos Terceiros adjustment — mirrors the committed third-party costs
  // on the cost side of the story (same hidden item as in the Lucro
  // Ajustado, but here it INCREASES the cost instead of decreasing profit).
  const custosTerceirosAdjustment = useMemo(() => {
    if (committedTerceiros.value <= 0) return undefined
    return {
      items: [
        {
          label: 'Custos não pagos',
          value: committedTerceiros.value,
          breakdown: committedTerceiros.breakdown,
        },
      ],
      adjustedLabel: 'Ajustado',
    }
  }, [committedTerceiros.value, committedTerceiros.breakdown])

  // Adjusted Lucro Líquido value — used by Saúde da Monumenta (PROA Lucro
  // and Margens). View-layer only (does not mutate `consolidatedData`).
  const lucroLiquidoAjustado = useMemo(() => {
    if (!consolidatedData) return undefined
    if (lucroTotalDeduction <= 0) return undefined
    return consolidatedData.lucroLiquido.real - lucroTotalDeduction
  }, [consolidatedData, lucroTotalDeduction])

  // Year-end projection trend based on historical seasonality (last 2 years).
  // Feeds the Lucro deduction so the projection stays consistent with the
  // adjusted Lucro card (no inflated projection from the folha-gap).
  const trendAnalysis = useTrendAnalysis({
    endDate,
    proaReceita: consolidatedData?.receita.proa ?? 0,
    proaFaturamento: consolidatedData?.faturamento.proa ?? 0,
    proaLucroLiquido: consolidatedData?.lucroLiquido.proa ?? 0,
    proaFolha: Math.abs(consolidatedData?.folha.proa ?? 0),
    proaTerceiros: Math.abs(consolidatedData?.custosServicoPrestado.proa ?? 0),
    proaDespesas: Math.abs(consolidatedData?.despesas.proa ?? 0),
    proaImpostos: Math.abs(consolidatedData?.impostos.proa ?? 0),
    proaReceitaFinanceira: consolidatedData?.receitaFinanceira.proa ?? 0,
    folhaMissingEstimate: folhaGap.totalEstimate,
    terceirosMissingEstimate: committedTerceiros.value,
    lucroLiquidoYTDDeduction: lucroTotalDeduction,
    enabled: !!endDate && !!consolidatedData,
  })

  // Fetch Client Matrix data via tRPC (same source as /financial/client-matrix page)
  const {
    data: clientMatrixData,
    isLoading: isClientMatrixLoading,
    refetch: refetchClientMatrix,
  } = trpc.dre.getClientMatrix.useQuery(
    { dataInicial: new Date(startDate), dataFinal: new Date(endDate) },
    { enabled: !!startDate && !!endDate, retry: false, staleTime: 5 * 60 * 1000 }
  )

  // Refetch callback for refresh button
  const refetch = useCallback(() => {
    void refetchDre()
    void refetchClientMatrix()
  }, [refetchDre, refetchClientMatrix])

  // Build monthly chart data using shared service
  const chartMonthlyData = useMonthlyChartData(dreData)

  // Transform client matrix data to match ClientRankingData format
  const clients = useMemo(() => {
    if (!clientMatrixData?.clients) return []
    return clientMatrixData.clients.map(c => ({
      clientName: c.nomeCliente ?? '',
      clientCode: c.codigoCliente ?? '',
      receitas: c.receita ?? 0,
      custos: c.custos ?? 0,
      resultado: c.margem ?? 0,
      projected: 0,
      proa: 0,
      despesas: 0,
      impostos: 0,
      freela: 0,
      empresaParceira: 0,
      repasse: 0,
      syncedAt: '',
      dataSource: 'trpc-client-matrix',
    }))
  }, [clientMatrixData])

  const isPainelLoading = isDreLoading || isClientMatrixLoading
  const painelError = dreError

  // Loading state - Skeleton
  if (isPainelLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header skeleton */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="h-10 w-48 bg-brand-dark-lighter rounded mb-2" />
            <div className="h-5 w-72 bg-brand-dark-lighter rounded" />
          </div>
        </div>

        {/* Filter bar skeleton */}
        <div className="h-14 bg-brand-dark-light rounded-lg" />

        {/* Receitas section skeleton */}
        <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-4">
          <div className="h-6 w-48 bg-brand-dark-lighter rounded mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-brand-dark-light rounded-xl p-4 space-y-3">
                <div className="h-4 w-24 bg-brand-dark-lighter rounded" />
                <div className="h-8 w-32 bg-brand-dark-lighter rounded" />
                <div className="h-3 w-full bg-brand-dark-lighter rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Custos section skeleton */}
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
          <div className="h-6 w-40 bg-brand-dark-lighter rounded mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-brand-dark-light rounded-xl p-4 space-y-3">
                <div className="h-4 w-24 bg-brand-dark-lighter rounded" />
                <div className="h-8 w-32 bg-brand-dark-lighter rounded" />
                <div className="h-3 w-full bg-brand-dark-lighter rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* Charts section skeleton */}
        <div className="space-y-4">
          <div className="h-6 w-48 bg-brand-dark-lighter rounded" />
          <div className="h-64 bg-brand-dark-light rounded-xl" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64 bg-brand-dark-light rounded-xl" />
            <div className="h-64 bg-brand-dark-light rounded-xl" />
          </div>
        </div>

        {/* Table section skeleton */}
        <div className="space-y-4">
          <div className="h-6 w-56 bg-brand-dark-lighter rounded" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-80 bg-brand-dark-light rounded-xl" />
            <div className="h-80 bg-brand-dark-light rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (painelError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
        <AlertTriangle className="h-16 w-16 text-warning animate-pulse" />
        <h1 className="text-3xl font-bold text-white font-display">Erro ao Carregar Dados</h1>
        <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
          Não foi possível carregar os dados do painel gerencial. Por favor, tente novamente.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary px-6 py-3"
        >
          Recarregar Página
        </button>
      </div>
    )
  }

  // No data state or invalid data structure
  if (!consolidatedData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <AlertTriangle className="h-12 w-12 text-brand-gray" />
        <p className="text-brand-gray-light font-body text-lg">Nenhum dado disponível</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Controls Panel - Period filters (FR1, FR2 - PRD Week 50) */}
      <DREControlsPanel
        startDate={startDate}
        endDate={endDate}
        onPeriodChange={(dates) => {
          setFilters({ startDate: dates.startDate, endDate: dates.endDate })
        }}
        onStartDateChange={(date) => setFilter('startDate', date)}
        onEndDateChange={(date) => setFilter('endDate', date)}
        onRefresh={refetch}
        isLoading={isPainelLoading}
        comparisonMode={comparisonMode}
        onComparisonModeChange={(mode) => setFilter('comparisonMode', mode)}
        comparisonRange={comparisonRange}
        granularity={granularity}
        onGranularityChange={(gran) => setFilter('granularity', gran)}
        showComparison={false}
        showGranularity={false}
        showProaComparison={false}
        showPresets={false}
      />

      {/* SAÚDE DA MONUMENTA */}
      <SaudeMonumentaSection
        faturamento={consolidatedData.faturamento}
        receitaBruta={consolidatedData.receita}
        lucroLiquido={consolidatedData.lucroLiquido}
        lucroLiquidoAjustado={lucroLiquidoAjustado}
        startDate={startDate}
        endDate={endDate}
        timesheetHorasPreenchidas={Math.round(countBusinessDays(startDate, endDate) * 8 * 0.49)}
        folhaReal={Math.abs(consolidatedData.folha.real)}
        faturamentoTrend={trendAnalysis.faturamento}
        receitaTrend={trendAnalysis.receita}
        receitaFinanceiraTrend={trendAnalysis.componentTrends.receitaFinanceira}
        folhaTrend={trendAnalysis.componentTrends.folha}
        terceirosTrend={trendAnalysis.componentTrends.terceiros}
        despesasTrend={trendAnalysis.componentTrends.despesas}
        impostosTrend={trendAnalysis.componentTrends.impostos}
        lucroLiquidoTrend={trendAnalysis.lucroLiquido}
      />

      {/* RECEITAS - Performance de Receitas */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold text-brand-gray flex items-center gap-2">
            <TrendingUp size={20} strokeWidth={1.5} />
            Performance de Receitas
          </h3>
          <MargensHealthCard
            faturamentoReal={consolidatedData.faturamento.real}
            faturamentoPrev={consolidatedData.faturamento.previousValue}
            receitaBrutaReal={consolidatedData.receita.real}
            receitaBrutaPrev={consolidatedData.receita.previousValue}
            lucroLiquidoReal={lucroLiquidoAjustado ?? consolidatedData.lucroLiquido.real}
            lucroLiquidoPrev={consolidatedData.lucroLiquido.previousValue}
            isAdjusted={lucroLiquidoAjustado !== undefined}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <EnhancedMetricCard
            metric="faturamento"
            data={consolidatedData.faturamento}
            valueColor="white"
            trend={trendAnalysis.faturamento}
            periodKey={periodKey}
          />
          <EnhancedMetricCard
            metric="receita"
            label="Receita"
            data={consolidatedData.receita}
            valueColor="white"
            aboveTrend={<BURevenueSplitInline startDate={startDate} endDate={endDate} />}
            trend={trendAnalysis.receita}
            periodKey={periodKey}
            afterValue={encarteirados.value > 0 ? (
              <div className="mt-2 flex justify-between items-center text-xs">
                <span className="text-brand-gray">Encarteirados</span>
                <button
                  type="button"
                  onClick={() => setEncarteiradosModalOpen(true)}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-brand-dark-lighter hover:ring-1 hover:ring-white/50 transition-all text-brand-gray-light font-medium whitespace-nowrap"
                >
                  +<SensitiveValue mask="R$ ••••">
                    {formatCurrency(encarteirados.value)}
                  </SensitiveValue>
                  <HelpCircle size={11} strokeWidth={1.5} className="text-brand-gray" />
                </button>
              </div>
            ) : undefined}
          />
          <EncarteiradosModal
            open={encarteiradosModalOpen}
            onOpenChange={setEncarteiradosModalOpen}
            jobs={encarteirados.jobs}
          />
          <EnhancedMetricCard
            metric="receitaFinanceira"
            data={consolidatedData.receitaFinanceira}
            valueColor="white"
            trend={trendAnalysis.componentTrends.receitaFinanceira}
            periodKey={periodKey}
          />
          <EnhancedMetricCard
            metric="lucroLiquido"
            data={consolidatedData.lucroLiquido}
            adjustment={lucroAdjustment}
            trend={trendAnalysis.lucroLiquido}
            periodKey={periodKey}
          />
        </div>
      </div>

      {/* CUSTOS - Estrutura de Custos */}
      <div>
        <h3 className="text-lg font-semibold text-brand-gray mb-3 flex items-center gap-2">
          <Calculator size={20} strokeWidth={1.5} />
          Estrutura de Custos
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <EnhancedMetricCard
            metric="custosServicoPrestado"
            label="Custos Terceiros"
            data={consolidatedData.custosServicoPrestado}
            section="cost"
            adjustment={custosTerceirosAdjustment}
            trend={trendAnalysis.componentTrends.terceiros}
            periodKey={periodKey}
          />
          <EnhancedMetricCard
            metric="folha"
            data={consolidatedData.folha}
            section="cost"
            trend={trendAnalysis.componentTrends.folha}
            periodKey={periodKey}
          />
          <EnhancedMetricCard
            metric="despesas"
            data={consolidatedData.despesas}
            section="cost"
            trend={trendAnalysis.componentTrends.despesas}
            periodKey={periodKey}
          />
          <EnhancedMetricCard
            metric="impostos"
            data={consolidatedData.impostos}
            section="cost"
            trend={trendAnalysis.componentTrends.impostos}
            periodKey={periodKey}
          />
        </div>
      </div>

      {/* Seção de Gráficos Comparativos */}
      <section className="mb-10">
        {/* Sankey: money flow overview */}
        <div className="mb-6">
          <FluxoFinanceiroSankey
            faturamento={consolidatedData.faturamento.real}
            receitaBruta={consolidatedData.receita.real}
            receitaFinanceira={consolidatedData.receitaFinanceira.real}
            folha={Math.abs(consolidatedData.folha.real)}
            terceiros={Math.abs(consolidatedData.custosServicoPrestado.real)}
            despesas={Math.abs(consolidatedData.despesas.real)}
            impostos={Math.abs(consolidatedData.impostos.real)}
            lucroLiquido={consolidatedData.lucroLiquido.real}
            clients={clients}
            folhaAjustada={folhaGap.totalEstimate > 0 ? Math.abs(consolidatedData.folha.real) + folhaGap.totalEstimate : undefined}
            terceirosAjustado={committedTerceiros.value > 0 ? Math.abs(consolidatedData.custosServicoPrestado.real) + committedTerceiros.value : undefined}
            lucroAjustado={lucroLiquidoAjustado !== undefined ? lucroLiquidoAjustado : undefined}
          />
        </div>
        {/* Receita vs Custos + Distribuição de Custos side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <RevenueCostChart data={chartMonthlyData} />
          </div>
          <CostDistributionChart
            data={{
              folha: Math.abs(consolidatedData.folha.real),
              custosDiretos: Math.abs(consolidatedData.custosServicoPrestado.real),
              custosADM: Math.abs(consolidatedData.despesas.real),
              impostos: Math.abs(consolidatedData.impostos.real)
            }}
          />
        </div>
      </section>

      {/* Seção Analítica */}
      <section className="mb-10">
        <ClientRankingTable clients={clients} />
      </section>
    </div>
  )
}
